/**
 * Compute the viewport-relative bounding rect of the character at ``position``
 * in a textarea, using the standard mirror-div technique: an off-screen
 * ``<div>`` copies the textarea's text-layout-relevant CSS, a marker ``<span>``
 * is placed at the desired offset, and its ``getBoundingClientRect`` is
 * measured. The mirror is removed before returning.
 *
 * Used to anchor a caret-following popover (e.g. a ``/`` autocomplete menu) to
 * the exact glyph the user is typing at. Shared across features so the chat
 * composer and the skill-body editor measure carets identically.
 */
export function getCaretRect(
  textarea: HTMLTextAreaElement,
  position: number,
): DOMRect {
  const mirror = document.createElement("div");
  const computed = window.getComputedStyle(textarea);
  // Copy text-layout-relevant CSS via direct property assignment. setProperty
  // requires kebab-case names, so the obvious-looking `setProperty("boxSizing", …)`
  // is a silent no-op — which causes the mirror to fall back to default browser
  // metrics and the resulting caret position to drift from the real textarea
  // by several pixels per line. Direct assignment on .style accepts camelCase
  // and works the way you'd expect.
  mirror.style.boxSizing = computed.boxSizing;
  mirror.style.borderTopWidth = computed.borderTopWidth;
  mirror.style.borderRightWidth = computed.borderRightWidth;
  mirror.style.borderBottomWidth = computed.borderBottomWidth;
  mirror.style.borderLeftWidth = computed.borderLeftWidth;
  mirror.style.fontFamily = computed.fontFamily;
  mirror.style.fontSize = computed.fontSize;
  mirror.style.fontWeight = computed.fontWeight;
  mirror.style.fontStyle = computed.fontStyle;
  mirror.style.letterSpacing = computed.letterSpacing;
  mirror.style.lineHeight = computed.lineHeight;
  mirror.style.paddingTop = computed.paddingTop;
  mirror.style.paddingRight = computed.paddingRight;
  mirror.style.paddingBottom = computed.paddingBottom;
  mirror.style.paddingLeft = computed.paddingLeft;
  mirror.style.tabSize = computed.tabSize;
  mirror.style.textIndent = computed.textIndent;
  mirror.style.textTransform = computed.textTransform;
  mirror.style.wordSpacing = computed.wordSpacing;
  mirror.style.position = "absolute";
  mirror.style.visibility = "hidden";
  mirror.style.left = "-9999px";
  mirror.style.top = "0";
  mirror.style.width = computed.width;
  mirror.style.height = "auto";
  mirror.style.whiteSpace = "pre-wrap";
  mirror.style.overflowWrap = "break-word";
  mirror.style.overflow = "hidden";

  mirror.textContent = textarea.value.substring(0, position);
  const marker = document.createElement("span");
  // Non-empty content so the marker has a measurable box even at offset 0 or
  // immediately after a newline. The marker's left/top is what we return.
  marker.textContent = textarea.value.substring(position) || ".";
  mirror.appendChild(marker);

  document.body.appendChild(mirror);
  const textareaRect = textarea.getBoundingClientRect();
  const mirrorRect = mirror.getBoundingClientRect();
  const markerRect = marker.getBoundingClientRect();
  document.body.removeChild(mirror);

  const offsetX = markerRect.left - mirrorRect.left - textarea.scrollLeft;
  const offsetY = markerRect.top - mirrorRect.top - textarea.scrollTop;
  const top = textareaRect.top + offsetY;
  const left = textareaRect.left + offsetX;
  return {
    top,
    left,
    bottom: top + markerRect.height,
    right: left + markerRect.width,
    width: markerRect.width,
    height: markerRect.height,
    x: left,
    y: top,
    toJSON() {
      return this;
    },
  } as DOMRect;
}
